/**
 * Speed control use cases (ADR-8): freeze / normal / fast (0/1/3).
 * Four separate factory functions sharing SpeedControlDeps.
 */

import { setSpeed as domainSetSpeed } from "../../domain/session.js";
import type { SpeedMultiplier } from "../../domain/session.js";
import type { RenderPort } from "../ports.js";
import { sessionToView } from "../state-view.js";

export interface SpeedControlDeps {
  render: RenderPort;
  getSession(): import("../../domain/session.js").GameSession | null;
}

function updateView(deps: SpeedControlDeps): void {
  const session = deps.getSession();
  if (session) deps.render.update(sessionToView(session, "game"));
}

export function createPause(deps: SpeedControlDeps): () => void {
  return function pause() {
    const session = deps.getSession();
    if (session) {
      domainSetSpeed(session, 0);
      updateView(deps);
    }
  };
}

export function createResume(deps: SpeedControlDeps): () => void {
  return function resume() {
    const session = deps.getSession();
    if (session && session.speed === 0) {
      domainSetSpeed(session, 1);
      updateView(deps);
    }
  };
}

export function createSetSpeed(deps: SpeedControlDeps): (speed: SpeedMultiplier) => void {
  return function setSpeed(speed: SpeedMultiplier) {
    const session = deps.getSession();
    if (session) {
      domainSetSpeed(session, speed);
      updateView(deps);
    }
  };
}

export function createCycleSpeed(deps: SpeedControlDeps): () => void {
  return function cycleSpeed() {
    const session = deps.getSession();
    if (!session) return;
    const next: SpeedMultiplier = session.speed === 0 ? 1 : session.speed === 1 ? 3 : 0;
    domainSetSpeed(session, next);
    updateView(deps);
  };
}
